/* eslint-disable no-magic-numbers */
import MeetingPermissions from "@/permissions/MeetingPermissions";
import NOID from "@/constants/NOID";
import OutcomePermissions from "@/permissions/OutcomePermissions";
import OutcomeStatus from "@/constants/OutcomeStatus";
import OutcomeStatusV2 from "@/constants/OutcomeStatusV2";
import OutcomeType from "@/constants/OutcomeType";
import TextTriggers from "@/constants/TextTriggers";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import keyBy from "lodash/keyBy";
import moment from "moment-timezone";
import uuid from "uuid/v4";

export const isOutcomeValid = ( timezone, inviteeMap, outcome ) => {
	const isContentValid = !!( outcome.content.trim() );

	if ( outcome.type !== OutcomeType.COMMITMENT ) {
		return isContentValid;
	}

	let isDateValid = true;
	if ( outcome.dueDate ) {
		const date = moment.tz( outcome.dueDate, timezone );
		isDateValid = date.isValid();
	}

	let isAssignedInviteeValid = true;
	if ( outcome.assignments.length ) {
		isAssignedInviteeValid = outcome.assignments.reduce( ( isValid, assignment ) => {
			return isValid || ( assignment?.assigned?.personId in inviteeMap );
		}, false );
	}

	return isContentValid && isDateValid && isAssignedInviteeValid;
};

/**
 * @param {Outcome} outcome
 */
const parseCommand = outcome => {
	const { content, } = outcome;
	const trimmedContent = content.trim();
	const commandStart = trimmedContent.indexOf( "/" );
	const potentialCommand = commandStart !== 0 ?
		"" : trimmedContent.substr( commandStart );

	const command = {
		potentialCommand,
		commandOutcomeType: "",
		commandTrigger: "",
		hasPartialCommand: false,
		hasFullCommand: false,
		contentSansCommand: trimmedContent,
		contentWithCommand: trimmedContent,
	};

	// NOTE: the order here is intentional; it matches menu order in the UI
	[
		OutcomeType.NOTE,
		OutcomeType.DECISION,
		OutcomeType.COMMITMENT,
	].forEach( triggerOutcomeType => {
		if ( command.commandOutcomeType ) {
			return;
		}
		const commandTrigger = TextTriggers[ triggerOutcomeType ];
		if ( potentialCommand && commandTrigger.startsWith( potentialCommand ) ) {
			command.commandOutcomeType = triggerOutcomeType;
			command.commandTrigger = commandTrigger;
			command.hasPartialCommand = potentialCommand !== commandTrigger;
			command.hasFullCommand = potentialCommand === commandTrigger;
			command.contentSansCommand = trimmedContent.substring( 0, commandStart );
			command.contentWithCommand = command.contentSansCommand + commandTrigger;
		}
	} );

	return command;
};

export const createNewOutcome = outcomeType => {
	return {
		assignments: [],
		completed: null,
		content: "",
		created: null,
		creator: null,
		dueDate: null,
		id: NOID,
		topicId: "",
		type: outcomeType,
	};
};

export const create = ( { meeting, userProfile, timezone, } ) => {
	const meetingPermissions = new MeetingPermissions( {
		meeting,
		timezone,
		userProfile,
	} );

	return {
		createNewOutcome,
		createPermissions( outcome ) {
			const outcomePermissions = new OutcomePermissions( {
				outcome,
				userProfile,
				timezone,
				meetingPermissions,
			} );

			return outcomePermissions.resolve();
		},
		createField( pristineOutcome, currentOutcome = null ) {
			if ( !currentOutcome ) {
				currentOutcome = cloneDeep( pristineOutcome );
			}

			const command = parseCommand( currentOutcome );

			const isNew = currentOutcome.id === NOID;

			const isDirty = !isEqual( pristineOutcome, currentOutcome );

			const inviteeMap = keyBy( meeting.invitees, "personId" );
			const isValid = isOutcomeValid( timezone, inviteeMap, currentOutcome );

			const pristinePermissionedOutcome = {
				...pristineOutcome,
				permissions: this.createPermissions( pristineOutcome ),
			};

			const currentPermissionedOutcome = {
				...currentOutcome,
				permissions: this.createPermissions( currentOutcome ),
			};

			return {
				uiKey: uuid(),
				pristineOutcome: pristinePermissionedOutcome,
				currentOutcome: currentPermissionedOutcome,
				...command,
				id: currentOutcome.id,
				isDirty,
				isNew,
				isValid,
				topicId: currentOutcome.id,
				type: currentOutcome.type,
			};
		},
	};
};

export const resolveOutcomeStatus = ( isCompleted, version ) => {
	version = parseInt( version, 10 );
	if ( version === 1 ) {
		return isCompleted ? OutcomeStatus.COMPLETED : OutcomeStatus.PLANNED;
	}
	if ( version === 2 ) {
		return isCompleted ? OutcomeStatusV2.COMPLETE : OutcomeStatusV2.OUTSTANDING;
	}
	throw new Error( `invalid API version ${version}; cannot determine outcome status` );
};

export const mapOutcomeBody = ( outcome, timezone, apiVersion = 2 ) => {
	return {
		id: outcome.id || undefined,
		topicId: outcome.topicId || undefined,
		type: outcome.type,
		content: outcome.content.trim(),
		due: outcome.dueDate ?
			moment.tz( outcome.dueDate, timezone ).format() :
			null,
		assignees: outcome.assignments
			.filter( assignment => !!assignment?.assigned?.personId )
			.map( assignment => assignment.assigned.personId ),
		status: resolveOutcomeStatus( outcome.completed, apiVersion ),
	};
};



// WEBPACK FOOTER //
// ./src/store/modules/outcomeHelpers.js